import type { AxiosResponse } from "axios";
import axios from "axios";
import errorMessageBuilder from "@/services/middleware/interceptors/errors/errorMessageBuilder";
import type { ErrorResponse } from "@/types/api/response/ErrorResponse";
import { LOGIN_ROUTE, routesMap } from "@/components/routes/routes";
import { useAuthStore } from "@/zustand/authStore";
import { useNotificationStore } from "@/zustand/notificationsStore";

interface ErrorInterceptor {
  response: {
    status: number;
    data: Array<ErrorResponse>;
  };
  config: {
    url: string;
    method: string;
  };
  code?: string;
  message?: string;
}

const handleError = (errorMessage: string) => {
  useNotificationStore
    .getState()
    .error(errorMessage || "An error occurred. Please reload the page.");
};

export default {
  responseSuccess: (response: AxiosResponse) => response,
  responseError: (error: ErrorInterceptor) => {
    // Handle cancelled/aborted requests silently
    if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    // Handle timeout errors
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      useNotificationStore
        .getState()
        .error("Request timed out. Please try again.");
      return Promise.reject(error);
    }

    // Handle network errors
    if (!error.response) {
      // Only handle if it's not a cancelled request
      if (error.message !== "Request interrupted by user") {
        useNotificationStore
          .getState()
          .error("Network error. Please check your connection and try again.");
      }
      return Promise.reject(error);
    }

    const errorCode = error.response.status;
    const errorBody: Array<ErrorResponse> = error.response.data;

    switch (errorCode) {
      case 401:
        if (window.location.href.indexOf("/auth") < 0) {
          useAuthStore.getState().setLogout();
          window.location.href = LOGIN_ROUTE;
        }
        return Promise.reject(error);
      /**
       * Permissions error handling
       */
      case 403:
        if (error.config.method === "put") {
          // editing campaigns
          if (error.config.url.includes("campaign")) {
            useNotificationStore.getState().error("Error occurred");
          } else {
            useNotificationStore
              .getState()
              .error("Permission Error while editing");
          }
        } else {
          useNotificationStore
            .getState()
            .error(
              "Access denied. You don't have permission to perform this action."
            );
        }
        break;
      case 400:
      case 409:
        if (typeof errorBody === "string") {
          useNotificationStore.getState().error(errorBody);
        } else {
          errorBody.forEach((e) => {
            const errorMessage = errorMessageBuilder.build(e);
            handleError(errorMessage);
          });
        }
        break;
      case 404:
        useNotificationStore
          .getState()
          .error("We couldn’t find a session with this ID");
        break;
      case 418:
        useNotificationStore
          .getState()
          .error("This action is disabled for the demo account");
        break;
      case 429:
        useNotificationStore
          .getState()
          .error("Too many requests, please try again in a few seconds");
        break;
      case 423:
        useAuthStore.getState().setAccountLocked(true);
        break;
      case 450:
        window.location.href =
          routesMap.auth.trialEmailVerificationPending.path;
        break;
      case 500:
        useNotificationStore
          .getState()
          .error("An error occurred on our servers. Please reload the page.");
        break;
      case 503:
        useNotificationStore
          .getState()
          .error(
            "Sign-ups are currently unavailable. Please try again later or contact support if the issue persists."
          );
        break;
      case 504:
        useNotificationStore.getState().error("Error retrieving data");
        break;

      default:
        break;
    }

    return Promise.reject(error);
  },
};
